import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, Calendar, MapPin, Plus, X, Search, ExternalLink, Edit, CheckCircle, Upload, Camera, Download } from 'lucide-react';
import {
  fetchTechnicianPatients,
  fetchServiceIdByName,
  submitTechnicianServiceDone,
  fetchPatientByNirman,
  uploadSwikratiPhoto
} from "../components/api";







function CameraFileUpload({ label, fieldName, file, preview, cameraStream, onFileChange, onCameraCapture, onCapture, disabled }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}
      </label>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-3">
        {cameraStream ? (
          <div className="space-y-2">
            <video
              id={`video-${fieldName}`}
              autoPlay
              playsInline
              ref={(video) => {
                if (video && cameraStream) {
                  video.srcObject = cameraStream;
                }
              }}
              className="w-full rounded"
            />
            <button
              onClick={() => onCapture(fieldName)}
              className="w-full px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
            >
              Capture
            </button>
          </div>
        ) : preview ? (
          <img src={preview} alt="Preview" className="w-full h-32 object-cover rounded mb-2" />
        ) : (
          <>
            <input
              type="file"
              id={`file-${fieldName}`}
              onChange={(e) => onFileChange(e, fieldName)}
              className="hidden"
              accept="image/*"
              disabled={disabled}
            />
            <div className="flex flex-col items-center gap-2">
              <Upload className="h-8 w-8 text-gray-400" />
              <label
                htmlFor={`file-${fieldName}`}
                className="cursor-pointer text-xs text-indigo-600 hover:text-indigo-700"
              >
                Choose File
              </label>
              <button
                onClick={() => onCameraCapture(fieldName)}
                className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                disabled={disabled}
              >
                <Camera size={14} />
                Use Camera
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function NorthstarPatientDashboard() {
  const location = useLocation();
  const navigate = useNavigate();

  const { packageId, technicianId, campId, packageName } = location.state || {};

  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [cameraStream, setCameraStream] = useState(null);
const [photoPreview, setPhotoPreview] = useState(null);



  

  useEffect(() => {
    if (!packageId || !technicianId) {
      setError("Missing package or technician information.");
      setLoading(false);
      return;
    }
    fetchPatients();
  }, [packageId, technicianId]);

  const fetchPatients = async () => {
    try {
      const data = await fetchTechnicianPatients({ technicianId, packageId, campId });
      // Group services by patient
      const groupedPatients = groupPatientsByUniqueId(data);
      setPatients(groupedPatients);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };



  const startCamera = async () => {
  const stream = await navigator.mediaDevices.getUserMedia({
    video: { facingMode: "environment" },
  });
  setCameraStream(stream);
};

const stopCamera = () => {
  if (cameraStream) {
    cameraStream.getTracks().forEach(track => track.stop());
    setCameraStream(null);
  }
};

const handleCameraCapture = () => {
  const video = document.querySelector("video");
  const canvas = document.createElement("canvas");
  canvas.width = video.videoWidth;
  canvas.height = video.videoHeight;
  canvas.getContext("2d").drawImage(video, 0, 0);

  canvas.toBlob((blob) => {
    const file = new File([blob], "swikrati.jpg", { type: "image/jpeg" });
    setCapturedPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
    stopCamera();
  });
};



  


  // Group patients by unique_patient_id and combine their services
  const groupPatientsByUniqueId = (patientData) => {
    const grouped = {};
    
    patientData.forEach(patient => {
      const key = patient.unique_patient_id;
      
      if (!grouped[key]) {
        grouped[key] = {
          id: patient.id,
          name: patient.name,
          unique_patient_id: patient.unique_patient_id,
          nirman_number: patient.nirman_number,
          services: []
        };
      }
      
      // Add service with completion status
      grouped[key].services.push({
        name: patient.services,
        completed: patient.completed
      });
    });
    
    return Object.values(grouped);
  };

  const filteredPatients = patients.filter(patient =>
    patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    patient.unique_patient_id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getServiceRoute = (service) => {
    const normalized = service.trim().toLowerCase();

    const serviceRoutes = {
      'audiometry': '/audiometry',
      'ecg': '/ecg',
      'x-ray': '/xray',
      'pft': '/pft',
      'optometry': '/optometry',
      'doctor consultation': '/doctor-consultation',
      'pathology': '/pathology',
      'dental consultation': '/dental-consultation',
      'vitals': '/vitals',
      'form 7': '/form7',
      'bmd': '/bmd',
      'tetanus vaccine': '/tetanus-vaccine',
      'typhoid vaccine': '/typhoid-vaccine',
      'coordinator': '/coordinator',
      'cbc': '/cbc',
      'complete hemogram': '/complete-hemogram',
      'hemoglobin': '/hemoglobin',
      'urine routine': '/urine-routine',
      'stool examination': '/stool-examination',
      'lipid profile': '/lipid-profile',
      'kidney profile': '/kidney-profile',
      'lft': '/lft',
      'kft': '/kft',
      'random blood glucose': '/random-blood-glucose',
      'blood grouping': '/blood-grouping',
      'northstar_assesment_form': '/northstar-form',
      'northstar_assessment_form': '/northstar-form',
    };

    return serviceRoutes[normalized] || null;
  };

  const handleServiceClick = async (serviceName, patient, isCompleted) => {
    const trimmedService = serviceName.trim();
    const route = getServiceRoute(trimmedService);

    // Always fetch serviceId for all services
    let serviceId;
    try {
      serviceId = await fetchServiceIdByName(trimmedService);
    } catch (err) {
      alert(`Service ID not found for: "${trimmedService}"`);
      return;
    }

    // Special handling for ECG, X-ray, and PFT (services that are marked as done without forms)
    if (["ecg", "x-ray", "pft",'vaccine','blood'].includes(trimmedService.toLowerCase())) {
      if (isCompleted) {
        // Allow editing/unmarking completed services
        if (window.confirm("This service is already completed. Do you want to mark it as incomplete?")) {
          try {
            // You might need to implement an API call to unmark service as done
            // For now, we'll just refresh to show current status
            alert("Service status updated!");
            fetchPatients();
          } catch (err) {
            alert("Error: " + err.message);
          }
        }
      } else {
        // Mark as completed
        if (window.confirm("You want to mark this service as done?")) {
          try {
            await submitTechnicianServiceDone({
              patientId: patient.unique_patient_id,
              technicianId,
              serviceId
            });
            alert("Service marked as done!");
            // Refresh the patient list to update completion status
            fetchPatients();
          } catch (err) {
            alert("Error: " + err.message);
          }
        }
      }
      return; // Do not navigate for these services
    }

    if (!route) {
      alert(`No route defined for service: "${trimmedService}"`);
      return;
    }

    // For all other services (with forms), allow navigation regardless of completion status
    // This makes completed services editable
    navigate(route, {
      state: {
        patientId: patient.unique_patient_id,
        patientName: patient.name,
        technicianId: technicianId,
        serviceId: serviceId,
        isEditing: isCompleted // Pass this flag to indicate if we're editing a completed service
      }
    });
  };



  const handleQRClick = (patient) => {
  setSelectedPatient(patient);
  setShowPhotoModal(true);
  setCapturedPhoto(null);
};

const handlePhotoCapture = (e) => {
  const file = e.target.files[0];
  if (file) {
    setCapturedPhoto(file);
  }
};

const handlePhotoUpload = async () => {
  if (!capturedPhoto || !selectedPatient) return;
  
  setUploading(true);
  try {
    // ✅ CHANGE: Use nirman_number directly from selectedPatient
    const nirmanNumber = selectedPatient.nirman_number;
    
    // ✅ ADD: Validation check
    if (!nirmanNumber) {
      alert('Nirman number not found for this patient');
      return;
    }
    
    // Upload photo
    await uploadSwikratiPhoto(nirmanNumber, capturedPhoto);
    
    alert('Swikrati photo uploaded successfully!');
    setShowPhotoModal(false);
    setCapturedPhoto(null);
    setSelectedPatient(null);
  } catch (err) {
    alert('Error uploading photo: ' + err.message);
  } finally {
    setUploading(false);
  }
};



  const generateQRCode = (patientId) =>
    `https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=PATIENT_${patientId}`;

  // Calculate completion statistics for each patient
  const getPatientStats = (services) => {
    const total = services.length;
    const completed = services.filter(service => service.completed).length;
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600 text-lg">Loading patient data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="bg-white border border-red-300 p-6 rounded shadow">
          <p className="text-red-800 font-semibold mb-2">Error</p>
          <p className="text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">
            Patients - {packageName || "Package"} (Camp #{campId})
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800"
          >
            ← Back
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search patients by name or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {filteredPatients.length === 0 ? (
          <div className="bg-white border border-gray-300 p-6 rounded text-center text-gray-600">
            {searchTerm ? "No patients found matching your search." : "No patients found for this package."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border border-gray-300 shadow rounded-lg">
              <thead className="bg-blue-100">
                <tr>
                  <th className="text-left px-4 py-3 border-b">#</th>
                  <th className="text-left px-4 py-3 border-b">Patient Name</th>
                  <th className="text-left px-4 py-3 border-b">Patient ID</th>
                  <th className="text-left px-4 py-3 border-b">Progress</th>
                  <th className="text-left px-4 py-3 border-b">Services</th>
                  <th className="text-left px-4 py-3 border-b">Swikrati Photo</th>
                </tr>
              </thead>
              <tbody>
                {filteredPatients.map((patient, index) => {
                  const stats = getPatientStats(patient.services);
                  return (
                    <tr key={patient.unique_patient_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 border-b">{index + 1}</td>
                      <td className="px-4 py-3 border-b font-medium text-gray-900">{patient.name}</td>
                      <td className="px-4 py-3 border-b font-mono text-sm">{patient.nirman_number}</td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center space-x-2">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            stats.completed === stats.total 
                              ? 'bg-green-100 text-green-800 border border-green-200'
                              : stats.completed === 0
                                ? 'bg-red-100 text-red-800 border border-red-200'
                                : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          }`}>
                            {stats.completed === stats.total ? (
                              <>
                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                Complete
                              </>
                            ) : stats.completed === 0 ? (
                              <>
                                <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                                Not Started
                              </>
                            ) : (
                              <>
                                <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                                In Progress
                              </>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 ml-2">
                            {stats.completed}/{stats.total}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex flex-wrap gap-1">
                          {patient.services.map((service, i) => {
                            const trimmedService = service.name.trim();
                            const route = getServiceRoute(trimmedService);
                            const isCompleted = service.completed;
                            
                            return (
                              <span
                                key={i}
                                title={
                                  isCompleted 
                                    ? route 
                                      ? "Service completed - Click to edit" 
                                      : "Service completed - Click to manage"
                                    : route 
                                      ? "Click to open service form" 
                                      : "Form not available"
                                }
                                className={`text-xs px-2 py-1 rounded cursor-pointer relative transition-all duration-200 ${
                                  isCompleted
                                    ? 'bg-green-500 text-white hover:bg-green-600 shadow-md'
                                    : route
                                      ? 'bg-blue-200 text-blue-900 hover:bg-blue-300'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                                onClick={() => handleServiceClick(trimmedService, patient, isCompleted)}
                              >
                                {trimmedService}
                                {isCompleted && (
                                  <span className="absolute -top-1 -right-1 bg-green-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs shadow-sm">
                                    ✓
                                  </span>
                                )}
                                {isCompleted && route && (
                                  <span className="absolute -top-1 -left-1 bg-blue-600 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs shadow-sm">
                                    ✎
                                  </span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                     <td className="px-4 py-3 border-b">
  <button
    onClick={() => handleQRClick(patient)}
    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
  >
    Capture Photo
  </button>
</td>

                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
{showPhotoModal && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
      <h2 className="text-xl font-bold mb-4">Capture Swikrati Photo</h2>

      <CameraFileUpload
        label="Swikrati Photo"
        fieldName="swikrati"
        file={capturedPhoto}
        preview={photoPreview}
        cameraStream={cameraStream}
        onFileChange={(e) => {
          const file = e.target.files[0];
          setCapturedPhoto(file);
          setPhotoPreview(URL.createObjectURL(file));
        }}
        onCameraCapture={startCamera}
        onCapture={handleCameraCapture}
      />

      <div className="flex gap-2 mt-4">
        <button
          onClick={handlePhotoUpload}
          disabled={!capturedPhoto || uploading}
          className="flex-1 bg-green-500 text-white px-4 py-2 rounded"
        >
          {uploading ? "Uploading..." : "Upload"}
        </button>

        <button
          onClick={() => {
            stopCamera();
            setShowPhotoModal(false);
            setCapturedPhoto(null);
            setPhotoPreview(null);
          }}
          className="flex-1 bg-gray-500 text-white px-4 py-2 rounded"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default NorthstarPatientDashboard;